﻿import { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from 'components/AppIcon';

function UsersListModal({ open, users = [], onClose, title }) {
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return users;
    const term = searchTerm.toLowerCase();
    return users.filter(
      (user) =>
        user.first_name?.toLowerCase().includes(term) ||
        user.last_name?.toLowerCase().includes(term) ||
        user.employee_id?.toLowerCase().includes(term),
    );
  }, [users, searchTerm]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (open && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={dropdownRef}
          className="absolute right-0 mt-2 w-96 bg-white border border-border rounded-xl shadow-2xl z-[999] overflow-hidden"
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          {/* Header - Notification Style */}
          <div className="bg-gradient-to-r from-primary-50 to-primary-100 p-3 border-b border-border flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <Icon name="Users" size={16} color="#3B82F6" />
              <h4 className="text-sm font-semibold text-text-primary line-clamp-1">{title}</h4>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/50 rounded-lg transition-colors text-secondary-500"
            >
              <Icon name="X" size={16} />
            </button>
          </div>

          {/* Search Bar */}
          <div className="px-3 py-2 bg-white border-b border-border">
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-secondary-400">
                <Icon name="Search" size={12} />
              </div>
              <input
                type="text"
                className="block w-full rounded-lg border border-border bg-secondary-50 py-1.5 pl-8 pr-3 text-xs placeholder:text-secondary-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-all shadow-sm"
                placeholder="Search user..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Notifications List Style */}
          <div className="max-h-[300px] overflow-y-auto custom-scrollbar bg-white divide-y divide-border">
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className="relative p-3 cursor-pointer transition-all duration-300 bg-white hover:bg-secondary-50 group"
                >
                  <div className="flex space-x-2.5">
                    <div
                      className={`w-9 h-9 rounded-full     bg-primary-50 flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105`}
                    >
                      <span className="text-xs font-bold text-primary uppercase">
                        {user.first_name?.[0]}
                        {user.last_name?.[0]}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 pr-1">
                          <p className="text-xs font-semibold text-text-primary line-clamp-1">
                            {user.first_name} {user.last_name}
                          </p>
                          <p className="text-[10px] text-text-secondary mt-0.5 line-clamp-1">
                            ID: <span className="font-medium">{user.employee_id}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center bg-white">
                <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-secondary-100 to-secondary-200 rounded-full flex items-center justify-center">
                  {searchTerm ? (
                    <Icon name="Search" size={24} color="#94A3B8" />
                  ) : (
                    <Icon name="CheckCircle" size={24} color="#10B981" />
                  )}
                </div>
                <p className="text-secondary-600 text-xs font-semibold">
                  {searchTerm ? 'No results' : 'All caught up!'}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-border flex justify-between items-center bg-secondary-50/50">
            <span className="text-[10px] text-secondary-500 font-medium uppercase tracking-wider">
              {filteredUsers.length} Pending
            </span>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-border px-3 py-1 text-xs font-medium text-text-primary hover:bg-white transition-all bg-white shadow-sm"
            >
              Close
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default UsersListModal;
